import { authAPI } from '../lib/api';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  phone?: string;
  role: string;
  roles?: string[];
  tenantId?: string;
  avatarUrl?: string;
  emailVerified?: boolean;
  phoneVerified?: boolean;
}

export interface LoginResponse {
  data: {
    user: AuthUser;
    accessToken: string;
    refreshToken: string;
  };
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  phone?: string;
  tenantName?: string;
  referralCode?: string;
  acceptedTerms: boolean;
}

export interface LinkedMethods {
  email:  { linked: boolean; value: string;        verified: boolean };
  mobile: { linked: boolean; value: string | null; verified: boolean };
  google: { linked: boolean };
  apple:  { linked: boolean };
}

export interface ProfileUpdateRequest {
  name?: string;
  phone?: string;
  avatarUrl?: string;
}

export interface PasswordChangeRequest {
  currentPassword: string;
  newPassword: string;
}

export const authService = {
  /**
   * Login with email and password. `rememberMe` controls refresh-token lifetime server-side
   * (longer-lived when true) and, client-side, which storage the session persists to.
   */
  login: (email: string, password: string, rememberMe = true) =>
    authAPI.post<LoginResponse>('/v1/auth/login', { email, password, rememberMe }).then((r) => r.data),

  /**
   * Register a new account
   */
  register: (data: RegisterRequest) =>
    authAPI.post<LoginResponse>('/v1/auth/register', data).then((r) => r.data),

  /**
   * Logout current user
   */
  logout: () =>
    authAPI.post('/v1/auth/logout').then((r) => r.data),

  /**
   * Refresh access token
   */
  refreshToken: (refreshToken: string) =>
    authAPI.post('/v1/auth/refresh', { refreshToken }).then((r) => r.data),

  /**
   * Request password reset email
   */
  forgotPassword: (email: string) =>
    authAPI.post('/v1/auth/forgot-password', { email }).then((r) => r.data),

  /**
   * Reset password with token
   */
  resetPassword: (token: string, password: string) =>
    authAPI.post('/v1/auth/reset-password', { token, password }).then((r) => r.data),

  /**
   * Get current user profile
   */
  getProfile: () =>
    authAPI.get('/v1/users/me').then((r) => r.data),

  /**
   * Update current user profile
   */
  updateProfile: (data: ProfileUpdateRequest) =>
    authAPI.patch('/v1/users/me', data).then((r) => r.data),

  /**
   * Change current user password
   */
  changePassword: (currentPassword: string, newPassword: string) =>
    authAPI.post('/v1/auth/change-password', { currentPassword, newPassword }).then((r) => r.data),

  /**
   * Deactivate the current user's own account (password-confirmed, backend already existed --
   * no page anywhere in the app ever called it before this).
   */
  deactivateAccount: (password: string) =>
    authAPI.post('/v1/auth/deactivate', { password }).then((r) => r.data),

  /**
   * Get user by ID
   */
  getUser: (userId: string) =>
    authAPI.get(`/v1/users/${userId}`).then((r) => r.data),

  /**
   * Update user profile (admin)
   */
  updateUser: (userId: string, data: Partial<Omit<AuthUser, 'id'>>) =>
    authAPI.patch(`/v1/users/${userId}`, data).then((r) => r.data),

  /**
   * Suspend/unsuspend user (admin)
   */
  toggleUserSuspension: (userId: string, suspended: boolean) =>
    authAPI.patch(`/v1/users/${userId}`, { suspended }).then((r) => r.data),

  /**
   * List users (admin)
   */
  listUsers: (params?: { page?: number; limit?: number; role?: string; organizationId?: string; tenantId?: string }) =>
    authAPI.get('/v1/users', { params }).then((r) => r.data),

  /** Invite a team member (ORG_ADMIN inviting ORG_MANAGER/ORG_STAFF/another ORG_ADMIN within
   * their own tenant, or PLATFORM_ADMIN inviting across tenants) -- POST /v1/users/invite,
   * tenant-scoped server-side via tenantScope middleware. */
  inviteUser: (data: { email: string; name: string; role: 'ORG_ADMIN' | 'ORG_MANAGER' | 'ORG_STAFF' }) =>
    authAPI.post('/v1/users/invite', data).then((r) => r.data),

  /** Get current user's referral info (code + stats) */
  getReferralInfo: () =>
    authAPI.get('/v1/referrals/me').then((r) => r.data),

  /** Send invite email to a list of email addresses */
  sendInvites: (emails: string[]) =>
    authAPI.post('/v1/referrals/invite', { emails }).then((r) => r.data),

  /** Google OAuth login/register with idToken from Google. `tenantName` only matters for
   * first-time signup (creates the tenant + assigns ORG_ADMIN); ignored on existing-user login.
   * `acceptedTerms` only matters for first-time signup too -- ignored on existing-user login. */
  googleAuth: (idToken: string, tenantName?: string, acceptedTerms?: boolean) =>
    authAPI.post<LoginResponse>('/v1/auth/google', { idToken, tenantName, acceptedTerms }).then((r) => r.data),

  /** firstName/lastName: Apple only ever sends these once, on the user's very first
   * authorization (out-of-band from the id_token) — pass them through only when present.
   * `acceptedTerms` only matters for first-time signup -- ignored on existing-user login. */
  appleAuth: (idToken: string, tenantName?: string, firstName?: string, lastName?: string, acceptedTerms?: boolean) =>
    authAPI.post<LoginResponse>('/v1/auth/apple', { idToken, tenantName, firstName, lastName, acceptedTerms }).then((r) => r.data),

  /** Resend email verification link */
  resendVerification: (email: string) =>
    authAPI.post('/v1/auth/resend-verification', { email }).then((r) => r.data),

  /** Confirm an email-verification token (from the link sent by resendVerification / on
   * registration). Backend route takes the token as a path segment, not a query param. */
  verifyEmail: (token: string) =>
    authAPI.get(`/v1/auth/verify-email/${token}`).then((r) => r.data),

  /** Send OTP to a mobile number (for login or registration) */
  sendOtp: (phone: string, name?: string) =>
    authAPI.post('/v1/auth/otp/send', { phone, name }).then((r) => r.data),

  /** Verify OTP and login/register. `tenantName` and `acceptedTerms` only matter for first-time
   * signup (creates the tenant + assigns ORG_ADMIN, records consent); ignored on existing-user
   * login. */
  verifyOtp: (phone: string, otp: string, name?: string, referralCode?: string, tenantName?: string, acceptedTerms?: boolean) =>
    authAPI.post<LoginResponse>('/v1/auth/otp/verify', { phone, otp, name, referralCode, tenantName, acceptedTerms }).then((r) => r.data),

  /** Get which sign-in methods are linked to the current account */
  getLinkedMethods: () =>
    authAPI.get<{ data: LinkedMethods }>('/v1/auth/linked-methods').then((r) => r.data),

  /** Send OTP to link a mobile number (call sendOtp first, then this) */
  linkMobile: (phone: string, otp: string) =>
    authAPI.post('/v1/auth/mobile/link', { phone, otp }).then((r) => r.data),

  /** Remove mobile number from account */
  unlinkMobile: () =>
    authAPI.delete('/v1/auth/mobile/unlink').then((r) => r.data),

  /** Disconnect Google from account */
  unlinkGoogle: () =>
    authAPI.delete('/v1/auth/google/unlink').then((r) => r.data),

  unlinkApple: () =>
    authAPI.delete('/v1/auth/apple/unlink').then((r) => r.data),

  // ── Organizations (PLATFORM_ADMIN / PLATFORM_STAFF) ─────────────────────

  /** List organizations — accessible by PLATFORM_ADMIN and PLATFORM_STAFF */
  listOrganizations: (params?: { page?: number; limit?: number; search?: string }) =>
    authAPI.get('/v1/organizations', { params }).then((r) => r.data),

  /** Get a single organization by ID (includes adminUser) */
  getOrganization: (id: string) =>
    authAPI.get(`/v1/organizations/${id}`).then((r) => r.data),

  /** Get the organization linked to the currently authenticated user (ORG_ADMIN) */
  getMyOrganization: () =>
    authAPI.get('/v1/organizations/mine').then((r) => r.data),

  /** Update organization settings (allowPlatformStaffAccess / allowPlatformStaffEdit) */
  updateOrgSettings: (orgId: string, settings: { allowPlatformStaffAccess?: boolean; allowPlatformStaffEdit?: boolean }) =>
    authAPI.patch(`/v1/organizations/${orgId}/settings`, settings).then((r) => r.data),

  /** Create an organization — PLATFORM_ADMIN or PLATFORM_STAFF */
  createOrganization: (data: { orgName: string; adminName: string; adminEmail: string; description?: string }) =>
    authAPI.post('/v1/organizations', data).then((r) => r.data),


  // ── Platform Staff ────────────────────────────────────────────────────────

  /** Invite a new platform staff member */
  createPlatformStaff: (data: { email: string; name: string; phone?: string }) =>
    authAPI.post('/v1/platform-staff', data).then((r) => r.data),

  /** List platform staff members */
  listPlatformStaff: (params?: { page?: number; limit?: number; search?: string; isActive?: boolean }) =>
    authAPI.get('/v1/platform-staff', { params }).then((r) => r.data),

  /** Get a single platform staff member */
  getPlatformStaff: (id: string) =>
    authAPI.get(`/v1/platform-staff/${id}`).then((r) => r.data),

  /** Activate or deactivate a platform staff member */
  updatePlatformStaff: (id: string, isActive: boolean) =>
    authAPI.patch(`/v1/platform-staff/${id}`, { isActive }).then((r) => r.data),

  /** Soft-delete a platform staff member */
  deletePlatformStaff: (id: string) =>
    authAPI.delete(`/v1/platform-staff/${id}`).then((r) => r.data),

  /** Resend invite email to a platform staff member */
  resendStaffInvite: (id: string) =>
    authAPI.post(`/v1/platform-staff/${id}/resend-invite`).then((r) => r.data),

  /** Get a platform staff member's activity log */
  getPlatformStaffActivity: (id: string, page = 1, limit = 20) =>
    authAPI.get(`/v1/platform-staff/${id}/activity`, { params: { page, limit } }).then((r) => r.data),

  /** List a platform staff member's granted functional-area capabilities */
  listStaffCapabilities: (staffId: string) =>
    authAPI.get<{ data: Array<{ capability: string; grantedBy: string | null; createdAt: string }> }>(
      `/v1/platform-staff/${staffId}/capabilities`
    ).then((r) => r.data.data),

  /** Grant a functional-area capability to a platform staff member */
  grantStaffCapability: (staffId: string, capability: string) =>
    authAPI.post(`/v1/platform-staff/${staffId}/capabilities`, { capability }).then((r) => r.data),

  /** Revoke a functional-area capability from a platform staff member */
  revokeStaffCapability: (staffId: string, capability: string) =>
    authAPI.delete(`/v1/platform-staff/${staffId}/capabilities/${capability}`).then((r) => r.data),

  /** List the organizations a platform staff member is scoped to (and their access mode) */
  listStaffOrganizations: (staffId: string) =>
    authAPI.get<{
      data: {
        staffAccessMode: 'ALL_ORGS' | 'ASSIGNED_ORGS';
        assignments: Array<{ id: string; organization: { id: string; name: string } }>;
      };
    }>(`/v1/platform-staff/${staffId}/organizations`).then((r) => r.data.data),

  /** Assign an organization to a platform staff member (scopes them to ASSIGNED_ORGS) */
  assignStaffOrganization: (staffId: string, organizationId: string) =>
    authAPI.post(`/v1/platform-staff/${staffId}/organizations`, { organizationId }).then((r) => r.data),

  /** Unassign an organization from a platform staff member */
  unassignStaffOrganization: (staffId: string, organizationId: string) =>
    authAPI.delete(`/v1/platform-staff/${staffId}/organizations/${organizationId}`).then((r) => r.data),

  /** List an org-tier user's (ORG_MANAGER/ORG_STAFF) granted functional-area capabilities */
  listUserCapabilities: (userId: string) =>
    authAPI.get<{ data: Array<{ capability: string; grantedBy: string | null; createdAt: string }> }>(
      `/v1/users/${userId}/capabilities`
    ).then((r) => r.data.data),

  /** Grant a functional-area capability to an org-tier user */
  grantUserCapability: (userId: string, capability: string) =>
    authAPI.post(`/v1/users/${userId}/capabilities`, { capability }).then((r) => r.data),

  /** Revoke a functional-area capability from an org-tier user */
  revokeUserCapability: (userId: string, capability: string) =>
    authAPI.delete(`/v1/users/${userId}/capabilities/${capability}`).then((r) => r.data),
};
